import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import CustomCombobox from '@/shared/ui/custom/combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { AddNewTradeDialog } from '@/shared/forms';
import { useTrades, useCreateTrade } from '@/features/punch-lists/hooks';
import {
  useCreateAreaTag,
  useProjectAreaTags,
  useProjectDrawingSheets,
  useProjectLayouts,
  useProjectUsers,
  useRFIMutations,
} from '../hooks';
import type { RFIItem, RFIImpact, RFIPriority } from '../types';
import { RFI_IMPACT_LABELS, RFI_PRIORITY_LABELS } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number | undefined;
  item?: RFIItem | null;
}

const PRIORITIES = Object.keys(RFI_PRIORITY_LABELS) as RFIPriority[];
const IMPACTS = Object.keys(RFI_IMPACT_LABELS) as RFIImpact[];

interface AddNewLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchValue: string;
  onConfirm: (value: { name: string; layoutId: number }) => void;
  layouts: { id: number; name: string; type: string }[];
}

const AddNewLocationDialog: React.FC<AddNewLocationDialogProps> = ({
  open,
  onOpenChange,
  searchValue,
  onConfirm,
  layouts,
}) => {
  const [name, setName] = useState(searchValue);
  const [layoutId, setLayoutId] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setName(searchValue);
    setLayoutId('');
  }, [open, searchValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !layoutId) return;
    onConfirm({ name: name.trim(), layoutId: Number(layoutId) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Location</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="rfi-new-location-name">Name</Label>
            <Input
              id="rfi-new-location-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Level 2 - North Wing"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label>Floor Plan</Label>
            <Select value={layoutId} onValueChange={setLayoutId}>
              <SelectTrigger>
                <SelectValue placeholder="Select floor plan" />
              </SelectTrigger>
              <SelectContent>
                {layouts.map((layout) => (
                  <SelectItem key={layout.id} value={String(layout.id)}>
                    {layout.name}{layout.type ? ` (${layout.type})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || !layoutId}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const RfiFormDialog: React.FC<Props> = ({ open, onClose, projectId, item }) => {
  const { data: users = [] } = useProjectUsers(projectId);
  const { data: areaTags = [] } = useProjectAreaTags(projectId);
  const { data: layouts = [] } = useProjectLayouts(projectId);
  const { data: drawingSheets = [] } = useProjectDrawingSheets(projectId);
  const { data: trades = [] } = useTrades();
  const createAreaTag = useCreateAreaTag(projectId);
  const createTrade = useCreateTrade();
  const { createItem, updateItem } = useRFIMutations(projectId);
  const isEdit = !!item;

  const [form, setForm] = useState({
    title: '',
    question: '',
    priority: 'NORMAL' as RFIPriority,
    due_date: '',
    responsible_party_user: 'none',
    assignee_user: 'none',
    drawing_reference: 'none',
    location_reference: '',
    trade: '',
    spec_section_reference: '',
    suggested_solution: '',
    cost_impact: 'NONE' as RFIImpact,
    schedule_impact: 'NONE' as RFIImpact,
  });

  useEffect(() => {
    if (!item) {
      setForm({
        title: '',
        question: '',
        priority: 'NORMAL',
        due_date: '',
        responsible_party_user: 'none',
        assignee_user: 'none',
        drawing_reference: 'none',
        location_reference: '',
        trade: '',
        spec_section_reference: '',
        suggested_solution: '',
        cost_impact: 'NONE',
        schedule_impact: 'NONE',
      });
      return;
    }

    setForm({
      title: item.title,
      question: item.question,
      priority: item.priority,
      due_date: item.due_date ?? '',
      responsible_party_user: item.responsible_party_user ? String(item.responsible_party_user.id) : 'none',
      assignee_user: item.assignee_user ? String(item.assignee_user.id) : 'none',
      drawing_reference: item.drawing_reference ? String(item.drawing_reference.id) : 'none',
      location_reference: item.location_reference ? String(item.location_reference.id) : '',
      trade: item.trade ? String(item.trade.id) : '',
      spec_section_reference: item.spec_section_reference || '',
      suggested_solution: item.suggested_solution || '',
      cost_impact: item.cost_impact,
      schedule_impact: item.schedule_impact,
    });
  }, [item, open]);

  const canSubmit = useMemo(() => !!projectId && !!form.title.trim() && !!form.question.trim(), [projectId, form]);

  const handleAddNewLocation = async (value: { name: string; layoutId: number }) => {
    const newTag = await createAreaTag.mutateAsync({
      name: value.name,
      layout_id: value.layoutId,
    });
    setForm((prev) => ({ ...prev, location_reference: String(newTag.id) }));
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    setForm((prev) => ({ ...prev, trade: newTrade.id }));
  };

  const LocationAddNewDialog = React.useCallback(
    (props: Omit<AddNewLocationDialogProps, 'layouts'>) => (
      <AddNewLocationDialog {...props} layouts={layouts} />
    ),
    [layouts],
  );

  const handleSubmit = async () => {
    if (!projectId || !canSubmit) return;

    const payload = {
      project: projectId,
      title: form.title.trim(),
      question: form.question.trim(),
      priority: form.priority,
      due_date: form.due_date || null,
      responsible_party_user: form.responsible_party_user === 'none' ? null : Number(form.responsible_party_user),
      assignee_user: form.assignee_user === 'none' ? null : Number(form.assignee_user),
      drawing_reference: form.drawing_reference === 'none' ? null : form.drawing_reference,
      location_reference: form.location_reference ? Number(form.location_reference) : null,
      trade: form.trade || null,
      spec_section_reference: form.spec_section_reference.trim(),
      suggested_solution: form.suggested_solution.trim(),
      cost_impact: form.cost_impact,
      schedule_impact: form.schedule_impact,
    };

    if (isEdit && item) {
      await updateItem.mutateAsync({ id: item.id, data: payload });
    } else {
      await createItem.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit RFI' : 'Create RFI Draft'}</DialogTitle>
          <DialogDescription>
            Capture the field question, responsible party, and references for project coordination.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Example: Clarify fireproofing at stair core"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Question</Label>
            <Textarea
              value={form.question}
              onChange={(e) => setForm((prev) => ({ ...prev, question: e.target.value }))}
              placeholder="Describe the information needed to proceed work."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(value) => setForm((prev) => ({ ...prev, priority: value as RFIPriority }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>{RFI_PRIORITY_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm((prev) => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Responsible Party</Label>
              <Select
                value={form.responsible_party_user}
                onValueChange={(value) => setForm((prev) => ({ ...prev, responsible_party_user: value }))}
              >
                <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Assignee</Label>
              <Select
                value={form.assignee_user}
                onValueChange={(value) => setForm((prev) => ({ ...prev, assignee_user: value }))}
              >
                <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Drawing Reference</Label>
              <Select
                value={form.drawing_reference}
                onValueChange={(value) => setForm((prev) => ({ ...prev, drawing_reference: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select drawing sheet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {drawingSheets.map((sheet) => (
                    <SelectItem key={sheet.id} value={sheet.id}>
                      {sheet.label}{sheet.title ? ` - ${sheet.title}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Location Reference</Label>
              <CustomCombobox
                options={areaTags.map((tag) => ({ value: String(tag.id), label: tag.name }))}
                value={form.location_reference}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, location_reference: value || '' }))
                }
                placeholder="Select location"
                searchPlaceholder="Search locations..."
                addNewLabel="Add New Location"
                emptyMessage="No locations found."
                onAddNew={handleAddNewLocation}
                AddNewDialog={LocationAddNewDialog}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Spec Section Reference</Label>
              <Input
                value={form.spec_section_reference}
                onChange={(e) => setForm((prev) => ({ ...prev, spec_section_reference: e.target.value }))}
                placeholder="07 21 00"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Trade</Label>
              <CustomCombobox
                options={trades.map((trade) => ({ value: trade.id, label: trade.name }))}
                value={form.trade}
                onChange={(value) => setForm((prev) => ({ ...prev, trade: value || '' }))}
                placeholder="Select trade"
                searchPlaceholder="Search trades..."
                addNewLabel="Add New Trade"
                emptyMessage="No trades found."
                onAddNew={handleAddNewTrade}
                AddNewDialog={AddNewTradeDialog}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Cost Impact</Label>
              <Select
                value={form.cost_impact}
                onValueChange={(value) => setForm((prev) => ({ ...prev, cost_impact: value as RFIImpact }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IMPACTS.map((i) => (
                    <SelectItem key={i} value={i}>{RFI_IMPACT_LABELS[i]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Schedule Impact</Label>
              <Select
                value={form.schedule_impact}
                onValueChange={(value) => setForm((prev) => ({ ...prev, schedule_impact: value as RFIImpact }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IMPACTS.map((i) => (
                    <SelectItem key={i} value={i}>{RFI_IMPACT_LABELS[i]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Suggested Solution</Label>
            <Textarea
              value={form.suggested_solution}
              onChange={(e) => setForm((prev) => ({ ...prev, suggested_solution: e.target.value }))}
              placeholder="Optional recommended approach from field team."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || createItem.isPending || updateItem.isPending}>
            {isEdit ? 'Save Changes' : 'Create Draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
